import { Anchor, Button, Center, Loader, Stack } from '@mantine/core'
import { IconAlertTriangle, IconLock, IconMapQuestion } from '@tabler/icons-react'
import { EmptyState } from '@tnn/ui'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { isRouteErrorResponse, useRouteError } from 'react-router'
import { appLink } from './links'
import { useGoBack } from './stack'

export function Splash() {
  return (
    <Center h="100dvh" bg="var(--tnn-canvas)">
      <Loader type="dots" />
    </Center>
  )
}

/** A page outside this app (another app's, the one sign-in page): the browser loads it, the splash shows meanwhile */
export function LeaveFor({ href }: { href: string }) {
  useEffect(() => window.location.replace(href), [href])
  return <Splash />
}

export function ErrorScreen({ onRetry }: { onRetry?: () => void }) {
  const { t } = useTranslation()
  return (
    <Center mih="70dvh">
      <EmptyState
        icon={IconAlertTriangle}
        title={t('errors.generic')}
        body={t('errors.network')}
        actions={onRetry && <Button onClick={onRetry}>{t('actions.retry')}</Button>}
      />
    </Center>
  )
}

export function NoAccess() {
  const { t } = useTranslation()
  return (
    <Center mih="100dvh" bg="var(--tnn-canvas)">
      <EmptyState
        icon={IconLock}
        title={t('errors.forbidden')}
        body={t('errors.forbiddenApp')}
        actions={
          <Button component="a" href={appLink('portal')} variant="light">
            {t('apps.portal')}
          </Button>
        }
      />
    </Center>
  )
}

export function NotFound() {
  const { t } = useTranslation()
  const goBack = useGoBack()
  return (
    <EmptyState
      icon={IconMapQuestion}
      title={t('errors.pageNotFound')}
      actions={
        // Back to the page before, else this app's home (`/tms`), not Thananan OS's
        <Anchor component="button" onClick={() => goBack('/')} fw={500}>
          {t('actions.back')}
        </Anchor>
      }
    />
  )
}

export function RouteError() {
  const error = useRouteError()
  const { t } = useTranslation()
  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <Stack mih="100dvh" justify="center">
        <NotFound />
      </Stack>
    )
  }
  if (import.meta.env.DEV) console.error(error)
  return <ErrorScreen onRetry={() => window.location.reload()} key={t('errors.generic')} />
}
